/**
 * DashboardAdaptive — variante adaptive (JS-driven breakpoints)
 *
 * Mobile  (<768px)     → 1 colonna, cards compatte
 * Tablet  (768-1024px) → 2 colonne
 * Desktop (>1024px)    → 4 colonne (identico alla dashboard responsive attuale)
 *
 * Il layout cambia discretamente a ogni breakpoint, non in modo fluido.
 */
import { Card, Row, Col, Statistic, Space, Spin, Empty, Alert, Tag, Grid, theme } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  CarOutlined,
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { usePermission } from '@/hooks/usePermission'
import { useCMMStats } from '@/hooks/useCMM'
import WorkOrderInterventionsPanel from '@/components/WorkOrderInterventionsPanel'
import type { DashboardSummary } from '@/types'
import { INTERVENTION_STATUS_HEX as statusColors } from '@/constants/statuses'

const { useBreakpoint } = Grid

// Calcola la larghezza colonna in base al breakpoint (JS-driven, non CSS fluido)
function useColSpan() {
  const screens = useBreakpoint()
  if (!screens.md) return 24        // Mobile: 1 col
  if (!screens.xl) return 12        // Tablet: 2 col
  return 6                          // Desktop: 4 col
}

const CMMDashboardAdaptive = () => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { data: stats, isLoading, error } = useCMMStats()
  const colSpan = useColSpan()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '48px auto' }} />
  if (error) return <Alert message="Errore caricamento dashboard CMM" type="error" showIcon />
  if (!stats) return <Empty description="Nessun dato disponibile" />

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24 }}>Dashboard CMM</h2>

      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'} style={{ cursor: 'pointer' }} onClick={() => navigate('/schede-lavoro?stato=in%20approvazione')}>
            <Statistic title="Da Prendere in Carico" value={stats.work_orders_approvate} prefix={<FileTextOutlined />} valueStyle={{ color: token.colorPrimary }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'} style={{ cursor: 'pointer' }} onClick={() => navigate('/schede-lavoro?stato=in%20lavorazione')}>
            <Statistic title="In Lavorazione" value={stats.work_orders_in_lavorazione} prefix={<ToolOutlined />} valueStyle={{ color: token.colorWarning }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic title="Clienti Totali" value={stats.customers_total} prefix={<UserOutlined />} valueStyle={{ color: token.colorSuccess }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic title="Veicoli Totali" value={stats.vehicles_total} prefix={<CarOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
      </Row>

      <Card title={<Space><ClockCircleOutlined /><span>Interventi Meccanici</span><Tag color="blue">{stats.interventi_totali}</Tag></Space>}>
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
          {stats.interventi_per_stato.map((s: any) => (
            <Col span={isMobile ? 12 : colSpan === 6 ? 6 : 8} key={s.nome}>
              <Card size="small" bordered={false} style={{ background: token.colorFillAlter }}>
                <Statistic title={s.nome} value={s.totale} valueStyle={{ color: statusColors[s.nome] || token.colorTextSecondary, fontSize: isMobile ? '18px' : '20px' }} />
              </Card>
            </Col>
          ))}
          {stats.interventi_senza_stato > 0 && (
            <Col span={isMobile ? 12 : colSpan === 6 ? 6 : 8}>
              <Card size="small" bordered={false} style={{ background: token.colorWarningBg }}>
                <Statistic title="Da assegnare" value={stats.interventi_senza_stato} valueStyle={{ color: token.colorWarning, fontSize: isMobile ? '18px' : '20px' }} />
              </Card>
            </Col>
          )}
        </Row>
      </Card>
    </Space>
  )
}

const GeneralDashboardAdaptive = () => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const response = await axiosInstance.get<DashboardSummary>(API_ENDPOINTS.DASHBOARD_SUMMARY)
      return response.data
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })
  const colSpan = useColSpan()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '48px auto' }} />
  if (error) return <Alert message="Errore caricamento dashboard" type="error" showIcon />
  if (!summary?.stats) return <Empty description="Nessun dato disponibile" />

  const s = summary.stats

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24 }}>Dashboard</h2>

      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'} style={{ cursor: 'pointer' }} onClick={() => navigate('/schede-lavoro?stato=bozza')}>
            <Statistic title="Ordini Aperti" value={s.work_orders_open || 0} prefix={<ToolOutlined />} valueStyle={{ color: token.colorPrimary }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'} style={{ cursor: 'pointer' }} onClick={() => navigate('/schede-lavoro?stato=in_lavorazione')}>
            <Statistic title="In Lavorazione" value={s.work_orders_in_progress || 0} prefix={<ToolOutlined />} valueStyle={{ color: token.colorWarning }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic title="Clienti Totali" value={s.customers_total || 0} prefix={<UserOutlined />} valueStyle={{ color: token.colorSuccess }} />
          </Card>
        </Col>
        <Col span={colSpan}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Statistic title="Veicoli Totali" value={s.vehicles_total || 0} prefix={<CarOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
      </Row>

      {!!s.parts_low_stock && (
        <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
          <Col span={isMobile ? 24 : 12}>
            <Card size={isMobile ? 'small' : 'default'}>
              <Statistic title="Ricambi in Esaurimento" value={s.parts_low_stock} prefix={<CheckCircleOutlined />} valueStyle={{ color: token.colorError }} />
            </Card>
          </Col>
          <Col span={isMobile ? 24 : 12}>
            <Card size={isMobile ? 'small' : 'default'}>
              <Statistic title="Auto Cortesia Disponibili" value={s.courtesy_cars_available || 0} prefix={<CarOutlined />} valueStyle={{ color: token.colorSuccess }} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Pannello interventi solo su tablet/desktop */}
      {!isMobile && <WorkOrderInterventionsPanel />}
    </Space>
  )
}

export function DashboardAdaptive() {
  const isOperativo = usePermission('work_orders.operativo')
  const isFullView  = usePermission('dashboard.full_view')
  if (isOperativo && !isFullView) return <CMMDashboardAdaptive />
  return <GeneralDashboardAdaptive />
}

export default DashboardAdaptive
